import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useQueryClient } from "react-query";
import { MdOutlineVerifiedUser } from "react-icons/md";
import useMerchants from "../../../marketplace_admin/libs/useMerchants";
import useVerifyMerchant from "../../../marketplace_admin/libs/useVerifyMerchant";
import PageHeader from "../../components/PageHeader";
import FilterBar from "../../components/FilterBar";
import DataTable from "../../components/DataTable";
import EmptyState from "../../components/EmptyState";
import StatCard from "../../components/StatCard";
import useInfiniteScrollRef from "../../utils/useInfiniteScrollRef";
import Spinner from "../../../Spinner/Spinner";

const AdminVerificationPage = () => {
  const queryClient = useQueryClient();
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useMerchants();
  const verify = useVerifyMerchant();
  const [search, setSearch] = useState("");
  const lastRef = useInfiniteScrollRef(fetchNextPage, hasNextPage, isFetchingNextPage);

  const merchants = useMemo(
    () => data?.pages?.flatMap((p) => p.merchants || p.users || []) || [],
    [data]
  );

  const pending = useMemo(() => merchants.filter((m) => !m.isInfoVerify), [merchants]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return pending;
    return pending.filter((m) => {
      const name = `${m.first_name || ""} ${m.last_name || ""}`.toLowerCase();
      return name.includes(term) || (m.email || "").toLowerCase().includes(term);
    });
  }, [pending, search]);

  const handleVerify = (merchantId) => {
    verify.mutate(merchantId, {
      onSuccess: () => {
        queryClient.invalidateQueries("merchants");
        toast.success("Merchant verified");
      },
      onError: () => toast.error("Verification failed"),
    });
  };

  const columns = [
    {
      key: "name",
      label: "Merchant",
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}
          </div>
          <div style={{ fontSize: 13, color: "var(--dash-muted)" }}>{r.email}</div>
        </div>
      ),
    },
    {
      key: "products",
      label: "Products",
      render: (r) => r.products?.length ?? 0,
    },
    {
      key: "actions",
      label: "Actions",
      render: (r) => (
        <div className="dashboard-table-actions">
          <button
            type="button"
            className="dashboard-btn dashboard-btn--primary"
            onClick={() => handleVerify(r._id)}
            disabled={verify.isLoading}
          >
            Verify
          </button>
          <Link to={`/admin/merchants/${r._id}`} className="dashboard-btn dashboard-btn--secondary">
            Review
          </Link>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Verification queue"
        subtitle="Review and approve new merchant applications"
        icon={MdOutlineVerifiedUser}
        actions={
          <Link to="/admin/merchants" className="dashboard-btn dashboard-btn--secondary">
            All merchants
          </Link>
        }
      />

      {!isLoading && (
        <div className="dashboard-stat-grid">
          <StatCard label="Awaiting review" value={pending.length} />
          <StatCard label="Total loaded" value={merchants.length} />
        </div>
      )}

      <FilterBar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search pending merchants…" />

      {error && (
        <div className="dashboard-card">
          <p style={{ color: "var(--dash-danger)", margin: 0 }}>Could not load verification queue.</p>
        </div>
      )}

      {!isLoading && filtered.length === 0 && (
        <EmptyState
          icon={MdOutlineVerifiedUser}
          title="Queue empty"
          message={search ? "No pending merchants match your search." : "All merchants are verified."}
          actionLabel="View merchants"
          actionTo="/admin/merchants"
        />
      )}

      {filtered.length > 0 && (
        <DataTable columns={columns} rows={filtered.map((m) => ({ ...m, _key: m._id }))} loading={isLoading} />
      )}

      {!isLoading && filtered.length > 0 && (
        <div ref={lastRef} className="dashboard-load-more">
          {isFetchingNextPage && <Spinner />}
          {!hasNextPage && !isFetchingNextPage && (
            <span style={{ color: "var(--dash-muted)", fontSize: 13 }}>End of queue</span>
          )}
        </div>
      )}
    </>
  );
};

export default AdminVerificationPage;
